import { createSelector } from '@reduxjs/toolkit';
import { CommonRootState } from '@common/types/common-root-state-type.d';
import { Bot } from '@common-models/bot';
import { botSelectByQuery } from '@/common/reducers/DBServiceThunks';
import { DashboardStatsState } from './dashboardStatsSlice';
import { WebRootState } from '@website/store/web-store';

// Basic state selectors
const selectBotsState = (state: CommonRootState) => state.DBBotReducer;
const selectAllBotsData = createSelector(
    [selectBotsState],
    (botsState): Bot[] => botSelectByQuery(botsState, {})
);

// Dashboard Stats Slice selectors
export const selectDashboardStatsState = (state: WebRootState): DashboardStatsState => state.dashboardStats;

// Overview Stats selectors
export const selectOverviewState = createSelector(
    [selectDashboardStatsState],
    (dashboardStats) => dashboardStats.overview
);

export const selectOverviewData = createSelector(
    [selectOverviewState],
    (overviewState) => overviewState.data
);

export const selectOverviewStatsLoading = createSelector(
    [selectOverviewState],
    (overviewState) => overviewState.loading
);

export const selectOverviewStatsError = createSelector(
    [selectOverviewState],
    (overviewState) => overviewState.error
);

// Individual overview stats selectors with fallbacks to local data
export const selectTotalAgents = createSelector(
    [selectOverviewData, selectAllBotsData],
    (overviewData, bots) => overviewData?.totalAgents ?? bots.length
);

export const selectDeployedAgentsCount = createSelector(
    [selectOverviewData, selectAllBotsData],
    (overviewData, bots) => overviewData?.deployedAgents ?? bots.filter(bot => !!bot.activeDeploymentId).length
);

export const selectDraftAgentsCount = createSelector(
    [selectOverviewData, selectAllBotsData],
    (overviewData, bots) => overviewData?.draftAgents ?? bots.filter(bot => !bot.activeDeploymentId).length
);

export const selectActiveAgentsCount = createSelector(
    [selectOverviewData],
    (overviewData) => overviewData?.activeAgents ?? null
);

export const selectTotalSessions = createSelector(
    [selectOverviewData],
    (overviewData) => overviewData?.totalSessions ?? null
);

export const selectAverageNodesPerAgent = createSelector(
    [selectOverviewData],
    (overviewData) => overviewData?.averageNodesPerAgent ?? null
);

export const selectBotUsageGrowth = createSelector(
    [selectOverviewData],
    (overviewData) => overviewData?.botUsageGrowth ?? null
);

// Node Type Distribution selectors
export const selectNodeTypeDistributionState = createSelector(
    [selectDashboardStatsState],
    (dashboardStats) => dashboardStats.nodeTypeDistribution
);

export const selectNodeTypeDistributionData = createSelector(
    [selectNodeTypeDistributionState],
    (distributionState) => distributionState.data
);

export const selectNodeTypeDistributionLoading = createSelector(
    [selectNodeTypeDistributionState],
    (distributionState) => distributionState.loading
);

export const selectNodeTypeDistributionError = createSelector(
    [selectNodeTypeDistributionState],
    (distributionState) => distributionState.error
);

// Feature Adoption selectors
export const selectFeatureAdoptionState = createSelector(
    [selectDashboardStatsState],
    (dashboardStats) => dashboardStats.featureAdoption
);

export const selectFeatureAdoptionData = createSelector(
    [selectFeatureAdoptionState],
    (adoptionState) => adoptionState.data
);

export const selectFeatureAdoptionLoading = createSelector(
    [selectFeatureAdoptionState],
    (adoptionState) => adoptionState.loading
);

export const selectFeatureAdoptionError = createSelector(
    [selectFeatureAdoptionState],
    (adoptionState) => adoptionState.error
);

// Knowledge Base Adoption selectors
export const selectKbAdoptionState = createSelector(
    [selectDashboardStatsState],
    (dashboardStats) => dashboardStats.kbAdoption
);

export const selectKbAdoptionData = createSelector(
    [selectKbAdoptionState],
    (kbAdoptionState) => kbAdoptionState.data
);

export const selectKbAdoptionLoading = createSelector(
    [selectKbAdoptionState],
    (kbAdoptionState) => kbAdoptionState.loading
);

export const selectKbAdoptionError = createSelector(
    [selectKbAdoptionState],
    (kbAdoptionState) => kbAdoptionState.error
);

// --- Selectors for Node Event Stats ---
export const selectNodeEventStatsState = createSelector(
    [selectDashboardStatsState],
    (dashboardStats) => dashboardStats.nodeEventStats
);

export const selectNodeEventStatsData = createSelector(
    [selectNodeEventStatsState],
    (eventStatsState) => eventStatsState.data
);
// --- End Node Event Stats Selectors ---

// Dashboard last updated timestamp
export const selectDashboardLastUpdated = createSelector(
    [selectDashboardStatsState],
    (dashboardStats) => dashboardStats.lastUpdated
);

// Overall loading state
export const selectDashboardLoading = createSelector(
    [
        selectOverviewState,
        selectNodeTypeDistributionState,
        selectFeatureAdoptionState,
        selectKbAdoptionState,
        selectNodeEventStatsState
    ],
    (overview, distribution, feature, kb, events) => 
        overview.loading || distribution.loading || feature.loading || kb.loading || events.loading
); 